<template>
  <div>
    <TopBar
      parentTitle="Ingredients"
      currentTitle="Bulk Import"
      :backRoute="{ name: 'IngredientListView' }"
    />
    <div class="page-content">
      <div class="page-header">
        <div class="page-title-group">
          <h1 class="page-title">Bulk Import Ingredients</h1>
          <p class="page-subtitle">Import multiple ingredients from CSV data</p>
        </div>
      </div>

      <!-- CSV Input -->
      <FeedsCard title="CSV Data">
        <p class="form-hint mb-4">
          Paste CSV with headers: <code>name,item_type,is_food,default_unit,aisle_name</code><br>
          Valid item_type values: <code>ingredient</code>, <code>grocery</code>, <code>household</code>
        </p>
        <b-input
          v-model="csvInput"
          type="textarea"
          rows="10"
          placeholder="name,item_type,is_food,default_unit,aisle_name
toilet roll,household,false,pack,Household
oat milk,grocery,true,carton,Milk & Dairy"
          :disabled="isImporting"
        />
        <div class="mt-4">
          <label class="button is-primary is-light">
            <b-icon icon="upload" />
            <span>Upload .csv file</span>
            <input type="file" accept=".csv,text/csv" style="display: none" @change="onFileUpload" />
          </label>
          <b-button type="is-primary" outlined class="ml-2" :disabled="!csvInput || isImporting" @click="parseCSV">
            Parse & Validate
          </b-button>
        </div>
      </FeedsCard>

      <!-- Validation & Preview -->
      <FeedsCard v-if="parsedRows.length > 0" title="Preview" class="mt-5">
        <div v-if="errors.length > 0" class="validation-errors mb-4">
          <p class="has-text-danger has-text-weight-bold mb-2">{{ errors.length }} issue(s) found:</p>
          <ul>
            <li v-for="(error, i) in errors" :key="i" class="has-text-danger">Row {{ error.row }}: {{ error.message }}</li>
          </ul>
        </div>

        <div v-if="duplicates.length > 0" class="validation-warnings mb-4">
          <p class="has-text-warning-dark has-text-weight-bold mb-2">{{ duplicates.length }} duplicate(s) will be skipped:</p>
          <ul>
            <li v-for="(dup, i) in duplicates" :key="i" class="has-text-warning-dark">{{ dup }}</li>
          </ul>
        </div>

        <b-table :data="validRows" :hoverable="true">
          <b-table-column field="name" label="Name" v-slot="props">
            <strong>{{ props.row.name }}</strong>
          </b-table-column>
          <b-table-column field="item_type" label="Type" width="100" v-slot="props">
            <span class="type-tag" :class="props.row.item_type">{{ props.row.item_type }}</span>
          </b-table-column>
          <b-table-column field="is_food" label="Food?" width="60" centered v-slot="props">
            <b-icon :icon="props.row.is_food ? 'check' : 'close'" :type="props.row.is_food ? 'is-success' : 'is-danger'" size="is-small" />
          </b-table-column>
          <b-table-column field="default_unit" label="Default Unit" width="120" v-slot="props">
            {{ props.row.default_unit || '-' }}
          </b-table-column>
          <b-table-column field="aisle_name" label="Aisle" v-slot="props">
            <span :class="{ 'has-text-danger': !props.row.aisle }">
              {{ props.row.aisle ? props.row.aisle.name : props.row.aisle_name + ' (not found)' }}
            </span>
          </b-table-column>
          <template #empty>
            <div class="has-text-centered py-5">
              <p class="has-text-grey">No valid rows to import</p>
            </div>
          </template>
        </b-table>

        <div class="mt-4">
          <p class="has-text-weight-bold">{{ importableRows.length }} items ready to import</p>
        </div>
      </FeedsCard>

      <!-- Import Actions -->
      <FeedsCard v-if="importableRows.length > 0" title="Import" class="mt-5">
        <div v-if="isImporting">
          <b-progress :value="importProgress" :max="importableRows.length" type="is-primary" show-value>
            {{ importProgress }} / {{ importableRows.length }}
          </b-progress>
        </div>
        <div v-else-if="importComplete">
          <div class="result-box success">
            <p>Imported <strong>{{ importedCount }}</strong> items successfully.</p>
            <p v-if="skippedCount > 0"><strong>{{ skippedCount }}</strong> skipped (duplicates or errors).</p>
          </div>
        </div>
        <div v-else>
          <b-button type="is-primary" :disabled="importableRows.length === 0" @click="confirmImport">
            Import {{ importableRows.length }} Items
          </b-button>
        </div>
      </FeedsCard>
    </div>
  </div>
</template>

<script>
import db from "@/firebase/db"
import FeedsCard from "@/components/common/FeedsCard"
import TopBar from "@/components/layout/TopBar"

export default {
  name: 'BulkImportIngredientsView',
  components: {
    FeedsCard,
    TopBar
  },
  data() {
    return {
      csvInput: '',
      aisles: [],
      existingIngredients: [],
      parsedRows: [],
      errors: [],
      duplicates: [],
      isImporting: false,
      importProgress: 0,
      importComplete: false,
      importedCount: 0,
      skippedCount: 0
    }
  },
  firestore: {
    aisles: db.collection('ingredient_aisles').orderBy("position", "asc"),
    existingIngredients: db.collection('ingredients').orderBy("name", "asc")
  },
  computed: {
    validRows() {
      return this.parsedRows.filter(row => row.valid)
    },
    importableRows() {
      return this.parsedRows.filter(row => row.valid && row.aisle && !row.isDuplicate)
    }
  },
  methods: {
    onFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (e) => {
        this.csvInput = e.target.result
        this.parseCSV()
      }
      reader.readAsText(file)
    },
    parseCSV() {
      this.parsedRows = []
      this.errors = []
      this.duplicates = []
      this.importComplete = false

      const lines = this.csvInput.trim().split('\n')
      if (lines.length < 2) {
        this.$buefy.toast.open({ type: 'is-warning', message: 'CSV must have a header row and at least one data row' })
        return
      }

      const header = lines[0].split(',').map(h => h.trim().toLowerCase())
      const requiredHeaders = ['name', 'item_type', 'is_food', 'aisle_name']
      const missing = requiredHeaders.filter(h => !header.includes(h))
      if (missing.length > 0) {
        this.$buefy.toast.open({ type: 'is-danger', message: `Missing required headers: ${missing.join(', ')}` })
        return
      }

      const existingNames = new Set(this.existingIngredients.map(i => i.name?.toLowerCase()))
      const seenNames = new Set()
      const validTypes = ['ingredient', 'grocery', 'household']

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim()
        if (!line) continue

        const values = this.parseCSVLine(line)
        const row = {}
        header.forEach((h, idx) => {
          row[h] = values[idx]?.trim() ?? ''
        })

        const rowNum = i + 1
        let valid = true

        // Validate name
        const name = row.name?.toLowerCase().trim()
        if (!name) {
          this.errors.push({ row: rowNum, message: 'Name is empty' })
          valid = false
        }

        // Validate item_type
        if (!validTypes.includes(row.item_type)) {
          this.errors.push({ row: rowNum, message: `Invalid item_type "${row.item_type}" — must be: ${validTypes.join(', ')}` })
          valid = false
        }

        // Parse is_food
        const isFood = row.is_food?.toLowerCase() === 'true'

        // Resolve aisle
        const aisleMatch = this.aisles.find(a => a.name.toLowerCase() === row.aisle_name?.toLowerCase())
        if (!aisleMatch && valid) {
          this.errors.push({ row: rowNum, message: `Aisle "${row.aisle_name}" not found. Create it first.` })
        }

        // Check duplicates
        let isDuplicate = false
        if (name && existingNames.has(name)) {
          isDuplicate = true
          this.duplicates.push(`"${name}" already exists`)
        } else if (name && seenNames.has(name)) {
          isDuplicate = true
          this.duplicates.push(`"${name}" appears multiple times in CSV`)
        }
        if (name) seenNames.add(name)

        this.parsedRows.push({
          name: name,
          item_type: row.item_type,
          is_food: isFood,
          default_unit: row.default_unit || null,
          aisle_name: row.aisle_name,
          aisle: aisleMatch ? { id: aisleMatch.id, name: aisleMatch.name, position: aisleMatch.position } : null,
          valid: valid,
          isDuplicate: isDuplicate
        })
      }

      if (this.parsedRows.length === 0) {
        this.$buefy.toast.open({ type: 'is-warning', message: 'No data rows found in CSV' })
      }
    },
    parseCSVLine(line) {
      // Simple CSV parser that handles quoted fields with commas
      const result = []
      let current = ''
      let inQuotes = false

      for (let i = 0; i < line.length; i++) {
        const char = line[i]
        if (char === '"') {
          inQuotes = !inQuotes
        } else if (char === ',' && !inQuotes) {
          result.push(current)
          current = ''
        } else {
          current += char
        }
      }
      result.push(current)
      return result
    },
    confirmImport() {
      this.$buefy.dialog.confirm({
        title: 'Confirm Import',
        message: `This will create <strong>${this.importableRows.length}</strong> new ingredient documents.<br><br><strong>Are you sure?</strong>`,
        confirmText: 'Import',
        type: 'is-primary',
        hasIcon: true,
        onConfirm: () => this.runImport()
      })
    },
    async runImport() {
      this.isImporting = true
      this.importProgress = 0
      this.importedCount = 0
      this.skippedCount = this.parsedRows.length - this.importableRows.length

      try {
        const BATCH_SIZE = 500

        for (let i = 0; i < this.importableRows.length; i += BATCH_SIZE) {
          const batch = db.batch()
          const chunk = this.importableRows.slice(i, i + BATCH_SIZE)

          chunk.forEach(row => {
            const ref = db.collection('ingredients').doc()
            batch.set(ref, {
              created_at: Date.now(),
              name: row.name,
              aisle: row.aisle,
              tips: [],
              item_type: row.item_type,
              is_food: row.is_food,
              default_unit: row.default_unit
            })
          })

          await batch.commit()
          this.importProgress += chunk.length
          this.importedCount += chunk.length
        }

        this.importComplete = true
        this.$buefy.toast.open({ type: 'is-success', message: `Imported ${this.importedCount} items!`, duration: 4000 })
      } catch (error) {
        this.$buefy.toast.open({ type: 'is-danger', message: `Import error: ${error.message}`, duration: 5000 })
      } finally {
        this.isImporting = false
      }
    }
  }
}
</script>

<style scoped>
.type-tag {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
}

.type-tag.ingredient {
  color: var(--feeds-green);
  background: var(--feeds-green-light, #e6f5f3);
}

.type-tag.grocery {
  color: #b45309;
  background: #fef3c7;
}

.type-tag.household {
  color: #6366f1;
  background: #eef2ff;
}

.validation-errors,
.validation-warnings {
  padding: 12px 16px;
  border-radius: var(--radius-md, 8px);
}

.validation-errors {
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.validation-warnings {
  background: #fffbeb;
  border: 1px solid #fde68a;
}

.validation-errors ul,
.validation-warnings ul {
  margin-left: 16px;
  list-style: disc;
}

.result-box {
  background: var(--bg-tertiary, #f8fafc);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md, 8px);
  padding: 16px;
}

.result-box.success {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

code {
  background: rgba(0,0,0,0.06);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 13px;
}
</style>
